/**
 * AppState - Enhanced State Management with Redux Patterns
 * 
 * This module provides a reactive state management system that combines
 * the original AppState functionality with Redux-style patterns including
 * dispatch, reducers, and middleware support.
 * 
 * @module core/AppState
 */

import EventBus from './EventBus.js';
import { AppEvents } from './EventBus.js';
import Config from './Config.js';
import StorageService from '../services/StorageService.js';

// Import Redux enhancements
import { rootReducer, shouldReducerHandle, getReducerForDomain } from '../store/reducers.js';
import { createDefaultMiddleware, composeMiddleware } from '../store/middleware.js';
import { isValidAction } from '../store/actions.js';

/**
 * @typedef {Object} ApplicationState
 * @property {Array} initiatives - Current initiatives database
 * @property {Object} strategies - Uploaded and emergent strategies
 * @property {string} currentTab - Active tab identifier
 * @property {Object} user - User settings and preferences
 * @property {Object} ui - UI state (loading, modals, etc.)
 * @property {Object} chat - Chat conversation state
 * @property {Object} portfolio - Portfolio analysis state
 * @property {Object} selection - Selection state across domains
 * @property {Object} system - System state (version, errors, etc.)
 */

class AppState {
  constructor() {
    console.log('🚀 Initializing Enhanced AppState with Redux patterns...');
    
    /**
     * The application state object
     * @type {ApplicationState}
     */
    this.state = {
      // Initiative Management
      initiatives: [],
      
      // Strategy Management
      strategies: {
        uploaded: null,
        emergent: null,
        current: null
      },
      
      // UI State
      ui: {
        currentTab: 'strategic-framework',
        loading: false,
        modalOpen: null,
        sidebarVisible: true,
        theme: 'light',
        toasts: []
      },
      
      // User Configuration
      user: {
        apiKey: null,
        model: 'gpt-4o',
        preferences: {
          enableDebug: false,
          enableAnalytics: false,
          autoSave: true
        }
      },
      
      // Chat State
      chat: {
        messages: [],
        conversationDepth: 0,
        context: null,
        typing: false
      },
      
      // Portfolio State
      portfolio: {
        analysis: null,
        conflicts: [],
        lastAnalyzed: null
      },
      
      // Selection State
      selection: {
        selectedInitiative: null
      },
      
      // System State
      system: {
        version: Config.APP_VERSION || '5.1.1',
        initialized: false,
        errors: []
      }
    };
    
    // Redux enhancement properties
    this.reducers = { root: rootReducer };
    this.middleware = null;
    this.devTools = null;
    this.actionHistory = [];
    this.maxHistorySize = 50;
    
    // Backward compatibility flags
    this.legacyMode = false;
    this.subscribers = new Map();
    
    // Enable Redux enhancements only if explicitly configured
    const enableRedux = (typeof window !== 'undefined' && window.__ENABLE_REDUX__ === true)
      || (Config.FEATURES && (Config.FEATURES.REDUX === true || Config.FEATURES.Redux === true));
    this.isReduxEnhanced = false;
    if (enableRedux) {
      this.initializeReduxPatterns();
    }
    
    // Initialize from localStorage if available
    this.loadPersistedState();
    
    // Subscribe to state changes for auto-save
    if (Config.FEATURES?.AUTO_SAVE) {
      this.enableAutoSave();
    }
    
    console.log('✅ Enhanced AppState initialized with Redux patterns');
  }
  
  /**
   * Initialize Redux patterns and middleware
   * @private
   */
  initializeReduxPatterns() {
    // Setup middleware pipeline
    this.middleware = this.createMiddlewarePipeline();
    
    // Setup Redux DevTools if available
    this.initializeDevTools();
    
    // Mark as Redux-enhanced
    this.isReduxEnhanced = true;
    
    console.log('🔧 Redux patterns initialized');
  }
  
  /**
   * Create middleware pipeline
   * @private
   */
  createMiddlewarePipeline() {
    const middlewares = createDefaultMiddleware();
    return composeMiddleware(middlewares)(this);
  }
  
  /**
   * Initialize Redux DevTools
   * @private
   */
  initializeDevTools() {
    if (typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__) {
      this.devTools = window.__REDUX_DEVTOOLS_EXTENSION__.connect({
        name: 'Strategic Intelligence Platform',
        features: {
          jump: true,
          skip: true,
          reorder: true,
          dispatch: true,
          test: true,
          persist: true
        },
        serialize: {
          options: {
            undefined: true,
            function: (fn) => fn.toString()
          }
        }
      });
      
      this.devTools.init(this.state);
      
      // Listen for DevTools actions
      this.devTools.subscribe((message) => {
        if (message.type === 'DISPATCH' && message.state) {
          this.state = JSON.parse(message.state);
          this.notifyStateChange(null, this.state, { devTools: true });
        }
      });
      
      console.log('🛠️ Redux DevTools connected');
    }
  }
  
  /**
   * Redux-style dispatch method
   * @param {Object|Function} action - Action object or thunk function
   * @returns {*} The result of the action
   */
  dispatch(action) {
    // Handle thunk functions (async actions)
    if (typeof action === 'function') {
      return action(this.dispatch.bind(this), this.getState.bind(this));
    }
    
    // Validate action (allow internal legacy actions)
    if (!isValidAction(action)) {
      if (action && action.type === 'DIRECT_STATE_UPDATE') {
        // Legacy internal action used by setState; allow without error
      } else {
        console.error('Invalid action dispatched:', action);
        return;
      }
    }
    
    // Add to action history
    this.addToHistory(action);
    
    // Process through middleware pipeline
    if (this.middleware) {
      return this.middleware((processedAction) => {
        return this.processAction(processedAction);
      })(action);
    } else {
      return this.processAction(action);
    }
  }
  
  /**
   * Process action through reducers
   * @private
   */
  processAction(action) {
    const oldState = this.cloneState(this.state);
    
    try {
      // Run action through root reducer
      const newState = rootReducer(this.state, action);
      
      // Apply state change if different
      if (newState !== this.state) {
        this.state = newState;
        this.notifyStateChange(oldState, newState, action);
      }
      
      return action;
      
    } catch (error) {
      console.error('Error processing action:', error);
      
      // Dispatch error action
      this.dispatch({
        type: 'APP_ERROR',
        payload: {
          message: error.message,
          stack: error.stack,
          action: action.type
        }
      });
      
      return action;
    }
  }
  
  /**
   * Get current state or a specific path
   * @param {string} [path] - Dot notation path (e.g., 'user.apiKey')
   * @returns {*} The state value
   */
  getState(path) {
    if (!path) return this.state;
    
    return path.split('.').reduce((obj, key) => obj?.[key], this.state);
  }
  
  /**
   * Set state with automatic change detection and notification
   * BACKWARD COMPATIBILITY METHOD - converts to Redux action
   * @param {Object|Function} updates - State updates or updater function
   * @param {boolean} [persist=true] - Whether to persist to localStorage
   */
  setState(updates, persist = true) {
    // Handle function updater pattern
    if (typeof updates === 'function') {
      updates = updates(this.state);
    }
    
    // Convert to Redux action for processing
    const action = {
      type: 'DIRECT_STATE_UPDATE',
      payload: updates,
      meta: { 
        persist, 
        timestamp: Date.now(),
        legacy: true 
      }
    };
    
    return this.dispatch(action);
  }
  
  /**
   * Subscribe to state changes
   * @param {string|Function} pathOrCallback - Path to watch or callback for all changes
   * @param {Function} [callback] - Callback when path changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(pathOrCallback, callback) {
    if (typeof pathOrCallback === 'function') {
      // Subscribe to all changes
      const unsubscribe = EventBus.on(AppEvents.STATE_CHANGED, pathOrCallback);
      this.subscribers.set(pathOrCallback, unsubscribe);
      return unsubscribe;
    } else {
      // Subscribe to specific path
      const unsubscribe = EventBus.on(`state:${pathOrCallback}`, callback);
      this.subscribers.set(`${pathOrCallback}:${callback}`, unsubscribe);
      return unsubscribe;
    }
  }
  
  /**
   * Notify subscribers of state changes
   * @private
   */
  notifyStateChange(oldState, newState, action) {
    const changes = this.detectChanges(oldState || {}, newState);
    
    // Emit main state change event
    EventBus.emit(AppEvents.STATE_CHANGED, { 
      changes, 
      oldState, 
      newState,
      action 
    });
    
    // Emit specific events for each changed path
    changes.forEach(path => {
      EventBus.emit(`state:${path}`, this.getState(path));
    });
    
    // Send to DevTools
    if (this.devTools && action) {
      this.devTools.send(action, newState);
    }
  }
  
  /**
   * Add action to history for debugging
   * @private
   */
  addToHistory(action) {
    this.actionHistory.push({
      action,
      timestamp: Date.now(),
      state: this.cloneState(this.state)
    });
    
    // Keep history size manageable
    if (this.actionHistory.length > this.maxHistorySize) {
      this.actionHistory.shift();
    }
  }
  
  /**
   * Get action history
   */
  getHistory() {
    return [...this.actionHistory];
  }
  
  /**
   * Deep clone state for history tracking
   * @private
   */
  cloneState(state) {
    try {
      return JSON.parse(JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to clone state:', error);
      return { ...state };
    }
  }
  
  /**
   * Deep merge objects
   * @private
   */
  deepMerge(target, source) {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }
  
  /**
   * Detect changes between two state objects
   * @private
   */
  detectChanges(oldState, newState, path = '') {
    const changes = [];
    
    for (const key in newState) {
      const currentPath = path ? `${path}.${key}` : key;
      
      if (typeof newState[key] === 'object' && newState[key] !== null && !Array.isArray(newState[key])) {
        // Recursively check nested objects
        changes.push(...this.detectChanges(oldState[key] || {}, newState[key], currentPath));
      } else if (oldState[key] !== newState[key]) {
        changes.push(currentPath);
      }
    }
    
    return changes;
  }
  
  /**
   * Load persisted state from StorageService
   * @private
   */
  loadPersistedState() {
    try {
      // Load initiatives
      const initiatives = StorageService.get('initiatives', []);
      if (initiatives.length > 0) {
        this.state.initiatives = initiatives;
      }
      
      // Load strategies
      const uploadedStrategy = StorageService.get('uploadedStrategy');
      const emergentStrategy = StorageService.get('emergentStrategy');
      if (uploadedStrategy || emergentStrategy) {
        this.state.strategies = {
          uploaded: uploadedStrategy,
          emergent: emergentStrategy,
          current: uploadedStrategy || emergentStrategy
        };
      }
      
      // Load user preferences
      const preferences = StorageService.get('userPreferences', {});
      this.state.user.preferences = { ...this.state.user.preferences, ...preferences };
      
      // Load UI state
      const uiState = StorageService.get('uiState', {});
      if (uiState.theme) this.state.ui.theme = uiState.theme;
      if (uiState.sidebarVisible !== undefined) this.state.ui.sidebarVisible = uiState.sidebarVisible;
      if (uiState.currentTab) this.state.ui.currentTab = uiState.currentTab;
      
      // Load chat messages only when conversation memory is enabled
      try {
        if (Config?.FEATURES?.CONVERSATION_MEMORY === true) {
          const chatMessages = StorageService.get('chatMessages', []);
          if (chatMessages.length > 0) {
            this.state.chat.messages = chatMessages;
          }
        }
      } catch (_) {}
      
      // Load API key
      const apiKey = StorageService.get('apiKey');
      if (apiKey) {
        this.state.user.apiKey = apiKey;
      }
      
      console.log('✅ State loaded from StorageService (v' + (StorageService.version || '5.1.1') + ')');
      
    } catch (error) {
      console.error('Failed to load persisted state:', error);
    }
  }
  
  /**
   * Persist state to StorageService
   */
  persistState() {
    try {
      // Use batch operations for better performance
      const batch = StorageService.batch ? StorageService.batch() : null;
      
      if (batch) {
        // Save initiatives
        if (this.state.initiatives.length > 0) {
          batch.set('initiatives', this.state.initiatives);
        }
        
        // Save strategies
        if (this.state.strategies.uploaded) {
          batch.set('uploadedStrategy', this.state.strategies.uploaded);
        }
        if (this.state.strategies.emergent) {
          batch.set('emergentStrategy', this.state.strategies.emergent);
        }
        
        // Save user preferences
        batch.set('userPreferences', this.state.user.preferences);
        
        // Save UI state
        batch.set('uiState', {
          theme: this.state.ui.theme,
          sidebarVisible: this.state.ui.sidebarVisible,
          currentTab: this.state.ui.currentTab
        });
        
        // Save chat messages only when conversation memory is enabled
        if (Config?.FEATURES?.CONVERSATION_MEMORY === true) {
          const recentMessages = this.state.chat.messages.slice(-50);
          batch.set('chatMessages', recentMessages);
        }
        
        // Save API key separately
        if (this.state.user.apiKey) {
          batch.set('apiKey', this.state.user.apiKey);
        }
        
        // Commit all changes
        const saved = batch.commit();
        
        if (saved > 0) {
          console.log(`💾 State saved (${saved} items)`);
        }
      } else {
        // Fallback to individual saves
        StorageService.set('initiatives', this.state.initiatives);
        StorageService.set('userPreferences', this.state.user.preferences);
        if (this.state.user.apiKey) {
          StorageService.set('apiKey', this.state.user.apiKey);
        }
        console.log('💾 State saved (fallback mode)');
      }
      
    } catch (error) {
      console.error('Failed to persist state:', error);
    }
  }
  
  /**
   * Enable auto-save on state changes
   * @private
   */
  enableAutoSave() {
    let saveTimeout;
    
    this.subscribe((data) => {
      // Skip auto-save for certain actions
      const skipAutoSave = ['SET_TYPING', 'SHOW_TOAST', 'HIDE_TOAST'];
      
      if (data.action && skipAutoSave.includes(data.action.type)) {
        return;
      }
      
      // Debounce saves to avoid excessive writes
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        this.persistState();
        console.log('💾 State auto-saved');
      }, 1000);
    });
  }
  
  /**
   * Reset state to defaults
   * @param {boolean} [clearStorage=true] - Also clear storage
   */
  reset(clearStorage = true) {
    const resetAction = {
      type: 'APP_RESET',
      payload: { clearStorage },
      meta: { timestamp: Date.now() }
    };
    
    if (clearStorage) {
      // Clear storage but preserve API key
      if (StorageService.clear) {
        StorageService.clear(['apiKey']);
      }
    }
    
    // Dispatch reset action
    this.dispatch(resetAction);
    
    EventBus.emit(AppEvents.STATE_RESET);
    console.log('🔄 State reset to defaults');
  }
  
  /**
   * BACKWARD COMPATIBILITY METHODS
   * These methods maintain the original AppState API
   */
  
  // Initiatives
  addInitiative(initiative) {
    const action = {
      type: 'ADD_INITIATIVE',
      payload: {
        id: Date.now(),
        createdAt: new Date().toISOString(),
        ...initiative
      },
      meta: { persist: true }
    };
    
    this.dispatch(action);
    return action.payload;
  }
  
  updateInitiative(id, updates) {
    this.dispatch({
      type: 'UPDATE_INITIATIVE',
      payload: { id, updates },
      meta: { persist: true }
    });
  }
  
  deleteInitiative(id) {
    this.dispatch({
      type: 'DELETE_INITIATIVE',
      payload: id,
      meta: { persist: true }
    });
  }
  
  // UI State
  setLoading(loading, message = null) {
    this.dispatch({
      type: 'SET_LOADING',
      payload: { isLoading: loading, message }
    });
  }
  
  openModal(modalName, props = {}) {
    this.dispatch({
      type: 'SHOW_MODAL',
      payload: { modalName, props }
    });
  }
  
  closeModal() {
    this.dispatch({
      type: 'HIDE_MODAL'
    });
  }
  
  // Chat
  addChatMessage(message) {
    this.dispatch({
      type: 'ADD_CHAT_MESSAGE',
      payload: {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...message
      },
      meta: { persist: true }
    });
  }
  
  clearChat() {
    this.dispatch({
      type: 'CLEAR_CHAT',
      meta: { persist: true }
    });
  }
  
  /**
   * Enhanced debugging methods
   */
  
  getStats() {
    return {
      actionsDispatched: this.actionHistory.length,
      stateSize: JSON.stringify(this.state).length,
      subscribers: this.subscribers.size,
      reduxEnhanced: this.isReduxEnhanced,
      devToolsConnected: !!this.devTools
    };
  }
  
  exportState() {
    return {
      state: this.state,
      history: this.actionHistory,
      timestamp: new Date().toISOString(),
      version: this.state.system.version
    };
  }
  
  importState(exportedState) {
    if (exportedState.state) {
      this.state = exportedState.state;
      this.notifyStateChange(null, this.state, { type: 'STATE_IMPORTED' });
      console.log('📥 State imported successfully');
    }
  }
}

// Create singleton instance
const appState = new AppState();

// Export both the class and instance
export { AppState, appState as default };

// Attach to window for debugging in development
if (import.meta.env?.DEV) {
  window.AppState = appState;
  window.AppStateClass = AppState;
  console.log('🔧 Enhanced AppState available at window.AppState for debugging');
  console.log('🔧 AppState stats:', appState.getStats());
}
